import React, {useEffect, useState, useRef} from 'react';
import {message, Button} from 'antd';
import {useLazyQuery} from '@apollo/client';
import {ReportFileInitialValue} from "./config/reportFileFilterOptions";
import {ReportFileType} from "./graphql/queries/getReportFileListingForCs";
import {useCsReportFileQuery, ReportFileFiltersType} from "./hooks/useCsReportFileQuery";
import {useReportOperators} from "./hooks/useReportOperators";
import CsReportFileFilter from "./components/CsReportFileFilter";
import CsReportFileColumns from "./components/CsReportFileColumns";
import {ReportFileTypeEnum} from "../../../enums/ReportFileEnum";
import {
    GET_BATCH_REPORT_DOWNLOAD_URLS_FOR_CS,
    GetBatchReportDownloadUrlsForCsResponse,
    GetBatchReportDownloadUrlsVariables
} from './graphql/queries/getBatchReportFileUrls';
import {useTranslation} from 'react-i18next';
import TablePaginationBar from '../../../components/TablePaginationBar';

const CsReportFilePage: React.FC = () => {
    const {t} = useTranslation();
    const [tempFilters, setTempFilters] = useState<ReportFileFiltersType>(ReportFileInitialValue);
    const [filters, setFilters] = useState<ReportFileFiltersType>(ReportFileInitialValue);
    const initializedRef = useRef(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(20);
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<ReportFileType[]>([]);
    const [downloadLoading, setDownloadLoading] = useState(false);
    const [getBatchDownloadUrls] = useLazyQuery<
        GetBatchReportDownloadUrlsForCsResponse,
        GetBatchReportDownloadUrlsVariables
    >(GET_BATCH_REPORT_DOWNLOAD_URLS_FOR_CS);

    // 先获取操作人数据和默认过滤条件
    const {
        operatorOptions,
        loading: operatorsLoading,
        defaultFilters
    } = useReportOperators();

    // 计算是否应该跳过查询：defaultFilters 未加载完成或还未初始化时跳过
    const shouldSkipQuery = !defaultFilters || !initializedRef.current;

    // 只有在操作人数据加载完成后才执行报表查询
    const {
        reportFileData,
        loading: reportLoading,
        refetch,
        totalElements,
        buildQueryVariables
    } = useCsReportFileQuery(filters, currentPage, pageSize, shouldSkipQuery);

    const handleSelectChange = (newSelectedRowKeys: React.Key[], newSelectedRows: ReportFileType[]) => {
        setSelectedRowKeys(newSelectedRowKeys);
        setSelectedRows(newSelectedRows);
    };

    // 当默认过滤条件加载完成时，初始化过滤器状态
    useEffect(() => {
        if (defaultFilters && !initializedRef.current) {
            setTempFilters(defaultFilters);
            setFilters(defaultFilters);
            initializedRef.current = true;
        }
    }, [defaultFilters]);


    const handleResetFilters = () => {
        //重置时使用defaultFilters或初始值，不强制设置类型
        const resetFilters = defaultFilters || ReportFileInitialValue;
        setTempFilters(resetFilters);
        setFilters(resetFilters);
        setCurrentPage(1);
    };

    const handleFilter = () => {
        setFilters(tempFilters);
        setCurrentPage(1);
    };

    // 添加useEffect监听状态变化后执行查询，跳过不符合条件的查询
    useEffect(() => {
        // 如果 defaultFilters 还未加载完成，跳过查询
        if (!defaultFilters) {
            return;
        }
        refetch(buildQueryVariables());
    }, [filters, currentPage, pageSize, defaultFilters]);
    
    // 处理分页变化
    const handlePageChange = (page: number, pageSize: number) => {
        setCurrentPage(page);
        setPageSize(pageSize);
    };

    // 生成文件名
    const generateFilename = (index: number, reportName?: string) => {
        const timestamp = new Date().toISOString().split('T')[0];
        if (reportName) {
            if (/\.(xlsx|xls|pdf|zip|csv|doc|docx|txt|bmp|jpe?g|png)$/i.test(reportName)) {
                return reportName;
            }
            return `${reportName}.xlsx`;
        }
        return `Report_${index + 1}_${timestamp}.xlsx`;
    };

    const downloadFile = async (url: string, filename: string) => {
        const res = await fetch(url);
        const blob = await res.blob();
        const blobUrl = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        // 释放内存
        URL.revokeObjectURL(blobUrl);
    };

    // 处理批量下载
    const handleBatchDownload = async () => {
        if (selectedRows.length === 0) {
            message.warning('Please select the report you wish to download');
            return;
        }

        setDownloadLoading(true);
        try {
            // 提取报表ID列表
            const reportIds = selectedRows.map(report => parseInt(report.id.toString(), 10));

            // 调用GraphQL查询获取下载URLs
            const { data } = await getBatchDownloadUrls({
                variables: { reportIds },
                fetchPolicy: 'network-only' // 确保从服务器获取最新数据
            });

            if (!data?.getBatchReportDownloadUrlsForCs) {
                message.error('Failed to obtain the download link.');
                return;
            }

            const downloadUrls = data.getBatchReportDownloadUrlsForCs;
            
            if (downloadUrls.length === 0) {
                message.warning('No available download link is available.');
                return;
            }

            console.log(`Start downloading ${downloadUrls.length} file，Selected ${selectedRows.length} reports`);
            
            // 提示用户批量下载开始
            if (downloadUrls.length > 1) {
                message.info(`Starting batch download of ${downloadUrls.length} files...`);
            }

            for (let i = 0; i < downloadUrls.length; i++) {
                const url = downloadUrls[i];
                const reportName = i < selectedRows.length ? selectedRows[i].name : undefined;
                const filename = generateFilename(i, reportName);

                try {
                    await downloadFile(url, filename); // 使用 fetch blob 下载
                    await new Promise(res => setTimeout(res, 500)); // 稍微延迟
                    message.success(`File ${i + 1} downloaded`);
                } catch (e) {
                    let errorMessage = 'Unknown error';
                    if (e instanceof Error) {
                        errorMessage = e.message;
                    } else if (typeof e === 'string') {
                        errorMessage = e;
                    } else {
                        errorMessage = JSON.stringify(e);
                    }
                    console.log(`Failed to download file  ${errorMessage}`);
                    message.error(`Failed to download file ${i + 1}`);
                }
            }

            message.success(`All ${downloadUrls.length} files download initiated successfully!`);

        } catch (error) {
            console.error('Batch download failed:', error);
            message.error('Batch download failed, please try again later');
        } finally {
            setDownloadLoading(false);
        }
    };

    return (
        <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">

                {/* 筛选条件 */}
                <CsReportFileFilter
                    filters={tempFilters}
                    setFilters={setTempFilters}
                    operatorOptions={operatorOptions}
                />

                {/* 批量操作区域 */}
                <div className="flex justify-end items-center gap-2 mb-3 pl-0">
                    <Button 
                        type="primary" 
                        onClick={handleFilter}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('central.download.filters.filter')}</span>
                    </Button>
                    <Button
                        type="primary"
                        onClick={handleBatchDownload}
                        loading={downloadLoading}
                        disabled={selectedRows.length === 0}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('central.download.filters.batchDownload')} ({selectedRows.length})</span>
                    </Button>
                    <Button 
                        onClick={handleResetFilters}
                        className="w-auto min-w-24"
                    >
                        <span className="font-bold">{t('central.download.filters.reset')}</span>
                    </Button>
                </div>

                {/* 报表列表 */}
                <CsReportFileColumns
                    reportFileData={reportFileData}
                    onSelectChange={handleSelectChange}
                    selectedRowKeys={selectedRowKeys}
                    loading={operatorsLoading || reportLoading}
                />

                {/* 合计和分页 */}
                <TablePaginationBar
                    total={totalElements}
                    current={currentPage}
                    pageSize={pageSize}
                    onChange={handlePageChange}
                    showQuickJumper={false}
                />
            </div>
    );
};

export default CsReportFilePage;